import { AuthErrorKind, FirebaseAuthErrorCode } from '@griply/types';

import { WRONG_PASSWORD_CODES } from './map-reauth-error';

const PROVIDER_CANCEL_CODES = [
  'ERR_REQUEST_CANCELED',
  'ERR_CANCELED',
  'SIGN_IN_CANCELLED',
];

const NETWORK_CODES = [
  FirebaseAuthErrorCode.NetworkRequestFailed,
  'functions/unavailable',
  'unavailable',
  // Google Play Services' NETWORK_ERROR status, forwarded as a string by the Android sign-in SDK.
  '7',
];

// RNFB on Android has no code for a socket that drops mid-request: the cause rides only in an auth/unknown message.
const SOCKET_FAILURE_CAUSES = [
  '[ Software caused connection abort ]',
  '[ Connection reset ]',
  '[ Broken pipe ]',
];

const KIND_BY_CODE = new Map<string, AuthErrorKind>([
  ...PROVIDER_CANCEL_CODES.map(
    (code) => [code, AuthErrorKind.Cancelled] as const,
  ),
  ...WRONG_PASSWORD_CODES.map(
    (code) => [code, AuthErrorKind.WrongCombination] as const,
  ),
  [FirebaseAuthErrorCode.UserNotFound, AuthErrorKind.WrongCombination],
  [FirebaseAuthErrorCode.EmailAlreadyInUse, AuthErrorKind.EmailTaken],
  [FirebaseAuthErrorCode.WeakPassword, AuthErrorKind.WeakPassword],
  [FirebaseAuthErrorCode.InvalidEmail, AuthErrorKind.InvalidEmail],
  [FirebaseAuthErrorCode.InvalidActionCode, AuthErrorKind.InvalidLink],
  [FirebaseAuthErrorCode.ExpiredActionCode, AuthErrorKind.InvalidLink],
  [FirebaseAuthErrorCode.TooManyRequests, AuthErrorKind.TooManyRequests],
  ...NETWORK_CODES.map((code) => [code, AuthErrorKind.Network] as const),
  [FirebaseAuthErrorCode.UserDisabled, AuthErrorKind.AccountDisabled],
  [
    FirebaseAuthErrorCode.AccountExistsWithDifferentCredential,
    AuthErrorKind.DifferentSignInMethod,
  ],
]);

export const getAuthErrorCode = (error: unknown): string => {
  const code = (error as { code?: unknown } | null)?.code;

  return typeof code === 'string' ? code : '';
};

const getMessage = (error: unknown): string => {
  const message = (error as { message?: unknown } | null)?.message;

  return typeof message === 'string' ? message : '';
};

export const isAuthSocketFailure = (error: unknown): boolean =>
  getAuthErrorCode(error) === FirebaseAuthErrorCode.Unknown &&
  SOCKET_FAILURE_CAUSES.some((cause) => getMessage(error).includes(cause));

export const mapAuthError = (error: unknown): AuthErrorKind => {
  const kind = KIND_BY_CODE.get(getAuthErrorCode(error));

  if (kind) {
    return kind;
  }

  if (isAuthSocketFailure(error)) {
    return AuthErrorKind.Network;
  }

  // beforeUserCreated rejects a legacy Auth0 email with this code as its message, not as the code.
  if (getMessage(error).includes(FirebaseAuthErrorCode.EmailAlreadyInUse)) {
    return AuthErrorKind.EmailTaken;
  }

  return AuthErrorKind.Unknown;
};
